import React, { useState, useEffect } from 'react';
import { Search, User, Package, Gift, Eye, Plus, Trash2, TrendingUp, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axiosInstance from '../../api/axios';
import { formatEUR } from '../../utils/format';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [stats, setStats] = useState(null);
  const [adminNoteDraft, setAdminNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Coupon form state
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount: 10,
    expires_days: 30
  });

  useEffect(() => {
    loadCustomers();
    loadStats();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadCustomers();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const loadStats = async () => {
    try {
      const response = await axiosInstance.get('/admin/customers/stats/overview');
      if (response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await axiosInstance.get(`/admin/customers/?search=${searchQuery}`);
      if (response.data) {
        setCustomers(response.data);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error('Fehler beim Laden der Kunden');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerDetails = async (customerId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/${customerId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCustomerDetails(data);
        setSelectedCustomer(customerId);
        setAdminNoteDraft(data?.customer?.admin_note || '');
      }
    } catch (error) {
      console.error('Error loading customer details:', error);
      toast.error('Fehler beim Laden der Kundendetails');
    }
  };

  const assignCoupon = async () => {
    if (!newCoupon.code.trim()) {
      toast.error('Bitte Coupon-Code eingeben');
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/${selectedCustomer}/coupons/assign?coupon_code=${newCoupon.code}&discount_percentage=${newCoupon.discount}&expires_days=${newCoupon.expires_days}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        toast.success('Coupon erfolgreich zugewiesen!');
        setShowCouponModal(false);
        setNewCoupon({ code: '', discount: 10, expires_days: 30 });
        loadCustomerDetails(selectedCustomer);
      } else {
        toast.error('Fehler beim Zuweisen des Coupons');
      }
    } catch (error) {
      console.error('Error assigning coupon:', error);
      toast.error('Fehler beim Zuweisen des Coupons');
    }
  };

  const deleteCoupon = async (couponId) => {
    if (!window.confirm('Coupon wirklich löschen?')) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/${selectedCustomer}/coupons/${couponId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        toast.success('Coupon gelöscht!');
        loadCustomerDetails(selectedCustomer);
      } else {
        toast.error('Fehler beim Löschen');
      }
    } catch (error) {
      console.error('Error deleting coupon:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const openDeleteModal = (customer) => {
    setCustomerToDelete(customer);
    setShowDeleteModal(true);
  };

  // Persönliche Admin-Notiz speichern
  const saveAdminNote = async () => {
    if (!selectedCustomer) return;
    try {
      setSavingNote(true);
      await axiosInstance.patch(`/admin/customers/${selectedCustomer}/note`, {
        admin_note: adminNoteDraft,
      });
      toast.success('Notiz gespeichert');
      loadCustomerDetails(selectedCustomer);
    } catch (e) {
      toast.error('Notiz konnte nicht gespeichert werden');
    } finally {
      setSavingNote(false);
    }
  };

  // DSGVO Art. 20 — Datenauskunft als ZIP herunterladen
  const downloadDsgvoExport = async () => {
    if (!selectedCustomer || !customerDetails?.customer) return;
    const email = customerDetails.customer.email;
    if (!window.confirm(
      `DSGVO-Datenauskunft für ${email} erstellen?\n\nEs wird eine ZIP-Datei mit allen Konto-, Bestell-, Rechnungs- und Newsletter-Daten dieses Kunden heruntergeladen. Diese kannst du dem Kunden per Email zuschicken.\n\nRechtsgrundlage: DSGVO Art. 20.`
    )) return;
    try {
      const res = await axiosInstance.get(`/admin/customers/${selectedCustomer}/dsgvo-export`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safe = email.replace('@', '-at-').replace('.', '_');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `dsgvo-export_${safe}_${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('DSGVO-Export heruntergeladen. Datei kann per Email an Kunden gesendet werden.');
    } catch (e) {
      toast.error('Export fehlgeschlagen');
    }
  };

  const deleteCustomer = async () => {
    if (!customerToDelete) return;

    try {
      const response = await axiosInstance.delete(`/admin/customers/${customerToDelete.id}`);
      
      if (response.status === 200) {
        toast.success(`Kunde "${customerToDelete.first_name} ${customerToDelete.last_name}" wurde gelöscht`);
        setShowDeleteModal(false);
        setCustomerToDelete(null);
        loadCustomers();
        loadStats();
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      if (error.response?.status === 400) {
        toast.error('Kunde kann nicht gelöscht werden (hat Bestellungen)');
      } else {
        toast.error('Fehler beim Löschen des Kunden');
      }
    }
  };

  // Als Testkunde markieren (Admin-Konten werden per Backend-Guard blockiert)
  const toggleTestFlag = async (customer) => {
    if (customer.role === 'admin') {
      toast.error('Admin-Konten können nicht als Test markiert werden');
      return;
    }
    const nextIsTest = !customer.is_test;
    const msg = nextIsTest
      ? `Kunde "${customer.email}" als TESTKUNDE markieren?\n\nWird bei der nächsten "Testkunden-Bereinigung" mit entfernt.`
      : `Test-Markierung von "${customer.email}" entfernen?`;
    if (!window.confirm(msg)) return;
    try {
      const r = await axiosInstance.patch(`/admin/customers/${customer.id}/mark-test`, { is_test: nextIsTest });
      toast.success(r.data.message);
      loadCustomers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aktualisierung fehlgeschlagen');
    }
  };

  // Alle Test-Kunden bereinigen (Admin-Konten IMMER geschützt, 3-fach)
  const cleanupTestCustomers = async () => {
    try {
      const dry = await axiosInstance.post('/admin/customers/cleanup-test-customers?dry_run=true');
      const n = dry.data.would_delete || 0;
      if (!n) {
        toast.info('Keine Test-Kunden zum Aufräumen. Alles sauber! ✓');
        return;
      }
      const preview = (dry.data.preview || []).slice(0, 10)
        .map(p => `• ${p.email || '(ohne Email)'}`)
        .join('\n');
      const ok = window.confirm(
        `${n} Testkunde(n) endgültig löschen?\n\n${preview}${n > 10 ? `\n…und ${n - 10} weitere` : ''}\n\n🛡️ ADMIN-KONTEN sind automatisch ausgeschlossen — dein Login und alle Admin-Zugänge bleiben unberührt.\n\nAktion ist nicht umkehrbar.`
      );
      if (!ok) return;
      const res = await axiosInstance.post('/admin/customers/cleanup-test-customers?dry_run=false');
      toast.success(res.data.message);
      loadCustomers();
      loadStats();
    } catch (e) {
      toast.error('Bereinigung fehlgeschlagen');
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" style={{ backgroundColor: '#F5EDE4', minHeight: '100vh' }}>
      {/* Goldbraune Überschrift-Leiste */}
      <div className="mb-4 p-3 md:p-4 flex items-center gap-3" style={{ backgroundColor: '#C4A084' }}>
        <div className="w-10 h-10 rounded flex items-center justify-center bg-white/90">
          <User className="h-5 w-5" style={{ color: '#C4A084' }} />
        </div>
        <div>
          <h1 className="text-lg md:text-xl font-bold text-white">Kundenübersicht</h1>
          <p className="text-xs md:text-sm text-white/80">Verwalten Sie Ihre Kunden und deren Bestellungen</p>
        </div>
      </div>

      {/* Section: Statistics - Goldbraune Überschrift */}
      <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
        <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0FFF0]">
          <TrendingUp className="h-4 w-4 text-[#60B060]" />
        </div>
        <h2 className="text-base font-semibold text-white">Statistiken</h2>
      </div>

      {/* Statistics Cards - Compact with very light pastel colors */}
      {stats && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          <Card className="rounded-none bg-sky-50/50 border-sky-100">
            <CardContent className="p-2">
              <div className="flex items-center gap-2">
                <User className="h-6 w-6 text-sky-500" />
                <div>
                  <p className="text-lg font-bold text-gray-900">{stats.total_customers}</p>
                  <p className="text-xs text-gray-600">Gesamtkunden</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-none bg-emerald-50/50 border-emerald-100">
            <CardContent className="p-2">
              <div className="flex items-center gap-2">
                <Package className="h-6 w-6 text-emerald-500" />
                <div>
                  <p className="text-lg font-bold text-gray-900">{stats.customers_with_orders}</p>
                  <p className="text-xs text-gray-600">Mit Bestellungen</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-none bg-violet-50/50 border-violet-100">
            <CardContent className="p-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-6 w-6 text-violet-500" />
                <div>
                  <p className="text-lg font-bold text-gray-900">{stats.new_customers_this_month}</p>
                  <p className="text-xs text-gray-600">Neu diesen Monat</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Section: Search - Goldbraune Überschrift */}
      <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
        <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0F8FF]">
          <Search className="h-4 w-4 text-[#60A0D0]" />
        </div>
        <h2 className="text-base font-semibold text-white">Kundensuche</h2>
      </div>

      {/* Search Bar + Datenbereinigung */}
      <div className="mb-6 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input
            type="text"
            placeholder="Kunde suchen (Name oder E-Mail)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-none border-[#C4A084]"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#E8DCC8]" data-testid="customers-cleanup-toolbar">
          <span className="text-xs font-semibold text-[#5D4E3C]">🧹 Datenbereinigung:</span>
          <Button
            size="sm"
            variant="outline"
            onClick={cleanupTestCustomers}
            className="rounded-none border-red-300 text-red-700 hover:bg-red-50 h-8 text-xs"
            title="Löscht alle Kunden mit is_test=true — Admin-Konten sind IMMER geschützt (3-fach)"
            data-testid="customers-cleanup-tests"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            Testkunden bereinigen
          </Button>
          <span className="text-[11px] text-[#9C8C7A]">
            🛡️ Admin-Konten bleiben immer unberührt.
          </span>
        </div>
      </div>

      {/* Section: Customer List - Goldbraune Überschrift */}
      <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
        <div className="w-6 h-6 rounded flex items-center justify-center bg-[#FFF8F0]">
          <User className="h-4 w-4 text-[#E89860]" />
        </div>
        <h2 className="text-base font-semibold text-white">Kundenliste</h2>
      </div>

      {/* Customers Table */}
      <Card className="rounded-none border-[#C4A084]">
        <CardHeader className="p-0">
          <div className="p-3" style={{ backgroundColor: '#C4A084' }}>
            <CardTitle className="text-white text-base">Alle Kunden ({customers.length})</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b">
                <tr className="text-left text-sm text-gray-600">
                  <th className="pb-3 font-medium">Name</th>
                  <th className="pb-3 font-medium">E-Mail</th>
                  <th className="pb-3 font-medium">Registriert</th>
                  <th className="pb-3 font-medium">Bestellungen</th>
                  <th className="pb-3 font-medium">Umsatz</th>
                  <th className="pb-3 font-medium">Coupons</th>
                  <th className="pb-3 font-medium">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.id} className="border-b hover:bg-gray-50">
                    <td className="py-4">
                      <div className="flex items-center gap-2">
                        <User className="h-5 w-5 text-gray-400" />
                        <span className="font-medium">
                          {customer.first_name} {customer.last_name}
                        </span>
                        {customer.admin_note && (
                          <span title="Notiz vorhanden" className="text-base" data-testid="admin-note-indicator">📝</span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 text-sm text-gray-600">{customer.email}</td>
                    <td className="py-4 text-sm text-gray-600">
                      {new Date(customer.created_at).toLocaleDateString('de-DE')}
                    </td>
                    <td className="py-4">
                      <Badge variant="outline">{customer.total_orders || 0}</Badge>
                    </td>
                    <td className="py-4 font-semibold text-gray-900">
                      {formatEUR((customer.total_spent || 0))}
                    </td>
                    <td className="py-4">
                      <Badge className="bg-purple-100 text-gray-900">
                        {customer.active_coupons || 0}
                      </Badge>
                    </td>
                    <td className="py-4">
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => loadCustomerDetails(customer.id)}
                          title="Details anzeigen"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {customer.role !== 'admin' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleTestFlag(customer)}
                            title={customer.is_test ? 'Test-Markierung entfernen' : 'Als Testkunde markieren'}
                            className={customer.is_test ? 'text-orange-600 border-orange-300 hover:bg-orange-50' : ''}
                            data-testid={`toggle-test-customer-${customer.id}`}
                          >
                            <span className="text-xs font-semibold">
                              {customer.is_test ? '✓ TEST' : '⚑'}
                            </span>
                          </Button>
                        )}
                        {customer.role === 'admin' && (
                          <span className="inline-flex items-center px-2 text-[10px] font-bold text-white bg-[#8B6F4D]" title="Admin-Konto (geschützt)">
                            ADMIN
                          </span>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50 hover:text-red-700"
                          onClick={() => openDeleteModal(customer)}
                          title="Kunde löschen"
                          disabled={customer.role === 'admin'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {customers.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <User className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p>Keine Kunden gefunden</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Customer Details Modal */}
      {selectedCustomer && customerDetails && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setSelectedCustomer(null);
            setCustomerDetails(null);
          }}
        >
          <div
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">
                    {customerDetails.customer.first_name} {customerDetails.customer.last_name}
                  </h2>
                  <p className="text-sm text-gray-600">{customerDetails.customer.email}</p>
                </div>
                <Button variant="ghost" onClick={() => {
                  setSelectedCustomer(null);
                  setCustomerDetails(null);
                }}>
                  ✕
                </Button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold">{customerDetails.statistics.total_orders}</p>
                    <p className="text-sm text-gray-600">Bestellungen</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {formatEUR(customerDetails.statistics.total_spent)}
                    </p>
                    <p className="text-sm text-gray-600">Gesamtumsatz</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {formatEUR(customerDetails.statistics.avg_order_value)}
                    </p>
                    <p className="text-sm text-gray-600">Ø Bestellwert</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {customerDetails.statistics.active_coupons}
                    </p>
                    <p className="text-sm text-gray-600">Aktive Coupons</p>
                  </CardContent>
                </Card>
              </div>

              {/* DSGVO Art. 20 — Datenauskunft */}
              <div className="bg-blue-50 border border-blue-200 p-3" data-testid="dsgvo-export-section">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">⚖️</div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900">DSGVO-Datenauskunft (Art. 20)</p>
                    <p className="text-xs text-blue-800 mt-0.5">
                      Wenn dieser Kunde eine Datenauskunft anfragt, kannst du hier eine komplette ZIP-Datei mit allen seinen Daten erzeugen und ihm per Email zusenden.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={downloadDsgvoExport}
                    className="rounded-none bg-blue-600 hover:bg-blue-700 text-white text-xs whitespace-nowrap"
                    data-testid="dsgvo-export-btn"
                  >
                    📥 Export erstellen
                  </Button>
                </div>
              </div>

              {/* Persönliche Admin-Notiz */}
              <div className="bg-[#FAF5EE] border border-[#D4C4B0] p-4" data-testid="admin-note-section">                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">📝</span>
                  <h3 className="font-semibold text-[#5D4E3C]">Persönliche Notiz</h3>
                  <span className="text-[10px] text-[#9C8C7A] italic ml-auto">nur du siehst das</span>
                </div>
                <textarea
                  value={adminNoteDraft}
                  onChange={(e) => setAdminNoteDraft(e.target.value.slice(0, 2000))}
                  placeholder='z.B. "Stammkundin, freut sich über Extra-Blume" oder "Reklamierte Sept 2025 wegen Größe, war sehr höflich"...'
                  className="w-full border border-[#D4C4B0] p-2 text-sm bg-white resize-none focus:outline-none focus:border-[#8B6F4D]"
                  rows={3}
                  maxLength={2000}
                  data-testid="admin-note-textarea"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[11px] text-[#9C8C7A]">
                    {adminNoteDraft.length}/2000 Zeichen
                    {customerDetails.customer?.admin_note_updated_at && (
                      <> · zuletzt geändert: {new Date(customerDetails.customer.admin_note_updated_at).toLocaleDateString('de-DE')}</>
                    )}
                  </span>
                  <Button
                    size="sm"
                    onClick={saveAdminNote}
                    disabled={savingNote || adminNoteDraft === (customerDetails.customer?.admin_note || '')}
                    className="rounded-none bg-[#8B6F4D] hover:bg-[#5D4E3C] text-white h-8 text-xs"
                    data-testid="save-admin-note-btn"
                  >
                    {savingNote ? 'Speichere...' : 'Notiz speichern'}
                  </Button>
                </div>
              </div>

              {/* Recent Orders */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Letzte Bestellungen
                  </h3>
                </div>
                <div className="space-y-2">
                  {customerDetails.orders.slice(0, 5).map((order) => (
                    <Card key={order.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">#{order.order_number || order.id.slice(0, 8)}</p>
                            <p className="text-sm text-gray-600">
                              {new Date(order.created_at).toLocaleDateString('de-DE')}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-gray-900">
                              {formatEUR((order.total || order.total_amount || 0))}
                            </p>
                            <Badge>{order.status}</Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {customerDetails.orders.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">Noch keine Bestellungen</p>
                  )}
                </div>
              </div>

              {/* Coupons */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-lg flex items-center gap-2 text-[#5D4E3C]">
                    <Gift className="h-5 w-5" />
                    Coupons
                  </h3>
                  <Button size="sm" onClick={() => setShowCouponModal(true)} className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0">
                    <Plus className="h-4 w-4 mr-1" />
                    Neuer Coupon
                  </Button>
                </div>
                <div className="space-y-2">
                  {customerDetails.coupons.map((coupon) => (
                    <Card key={coupon.id} className="rounded-none border-[#D4C4B0]">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-bold text-[#5D4E3C]">{coupon.code}</p>
                            <p className="text-sm text-[#8B7355]">
                              {coupon.discount_percentage}% Rabatt
                              {coupon.is_used ? ' (Verwendet)' : ' (Aktiv)'}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => deleteCoupon(coupon.id)}
                            className="rounded-none bg-[#FFEBEE] text-[#E57373] hover:bg-[#FFCDD2] border-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {customerDetails.coupons.length === 0 && (
                    <p className="text-sm text-[#8B7355] text-center py-4">Keine Coupons vorhanden</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Coupon Modal */}
      {showCouponModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <Card className="max-w-md w-full rounded-none" style={{ backgroundColor: '#F5EDE4' }}>
            <CardHeader>
              <CardTitle className="text-[#5D4E3C]">Neuen Coupon zuweisen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block text-[#5D4E3C]">Coupon-Code</label>
                <Input
                  value={newCoupon.code}
                  onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                  placeholder="z.B. WELCOME20"
                  className="rounded-none border-[#D4C4B0]"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block text-[#5D4E3C]">Rabatt (%)</label>
                <Input
                  type="number"
                  value={newCoupon.discount}
                  onChange={(e) => setNewCoupon({ ...newCoupon, discount: parseInt(e.target.value) })}
                  min="1"
                  max="100"
                  className="rounded-none border-[#D4C4B0]"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block text-[#5D4E3C]">Gültig für (Tage)</label>
                <Input
                  type="number"
                  value={newCoupon.expires_days}
                  onChange={(e) => setNewCoupon({ ...newCoupon, expires_days: parseInt(e.target.value) })}
                  min="1"
                  className="rounded-none border-[#D4C4B0]"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={assignCoupon} className="flex-1 rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0">
                  Zuweisen
                </Button>
                <Button onClick={() => setShowCouponModal(false)} className="rounded-none bg-[#FFEBEE] text-[#E57373] hover:bg-[#FFCDD2] border-0">
                  Abbrechen
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Customer Confirmation Modal */}
      {showDeleteModal && customerToDelete && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <Card className="max-w-md w-full rounded-none" style={{ backgroundColor: '#F5EDE4' }}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-[#E57373]">
                <AlertTriangle className="h-5 w-5" />
                Kunde löschen?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-[#FFEBEE] border border-[#FFCDD2] rounded-none">
                <p className="text-sm text-[#C62828]">
                  <strong>Achtung:</strong> Diese Aktion kann nicht rückgängig gemacht werden!
                </p>
              </div>
              
              <div className="p-3 bg-white rounded-none border border-[#D4C4B0]">
                <p className="font-medium text-[#5D4E3C]">{customerToDelete.first_name} {customerToDelete.last_name}</p>
                <p className="text-sm text-[#8B7355]">{customerToDelete.email}</p>
                {customerToDelete.total_orders > 0 && (
                  <p className="text-sm text-[#FF9800] mt-2">
                    ⚠️ Dieser Kunde hat {customerToDelete.total_orders} Bestellung(en)
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={deleteCustomer} 
                  className="flex-1 rounded-none bg-[#FFEBEE] text-[#E57373] hover:bg-[#FFCDD2] border-0"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Endgültig löschen
                </Button>
                <Button 
                  onClick={() => {
                    setShowDeleteModal(false);
                    setCustomerToDelete(null);
                  }}
                  className="flex-1 rounded-none bg-white text-[#5D4E3C] hover:bg-[#FAF8F5] border border-[#D4C4B0]"
                >
                  Abbrechen
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Customers;
